/* =========================================================
   Touch-device motion
   =========================================================
   Every card flourish on the site is written as a :hover state — the lift,
   the peach flood, the icon fill, the arrow nudge. A finger never fires any
   of them, so on a phone the whole design sits still.

   This gives those same states a second trigger. As a card scrolls through
   the middle of the screen it gets `.is-live`, and the component's CSS
   applies the accent to `.is-live` alongside `:hover`. The result is a
   spotlight that walks down the page as you scroll, one card at a time.

   Runs only where hovering is impossible. On a mouse — or a hybrid laptop,
   which reports `hover: hover` — the class is never applied and :hover keeps
   sole charge. Under `prefers-reduced-motion` it stays off entirely.
   ========================================================= */

const LIVE_CLASS = "is-live";
const SELECTOR = "[data-live]";

/* The band of the viewport that counts as "in the spotlight": from 30% down
   to 80% of the screen height. Narrow enough that cards light one at a time,
   deep enough that the last row of a grid still reaches it before the footer
   stops the scroll. */
const BAND = "-30% 0px -20% 0px";

/**
 * Start the in-view spotlight. Safe to call once at app start.
 * @returns {() => void} teardown — disconnects observers and clears the class.
 */
export default function initTouchMotion() {
  const supported =
    typeof window !== "undefined" &&
    "IntersectionObserver" in window &&
    "MutationObserver" in window &&
    typeof window.matchMedia === "function";

  if (!supported) return () => {};

  const canHover = window.matchMedia("(hover: hover)");
  const reduced = window.matchMedia("(prefers-reduced-motion: reduce)");

  let spotlight = null;
  let mounts = null;
  let rescanQueued = false;

  const shouldRun = () => !canHover.matches && !reduced.matches;

  function scan() {
    // Re-observing an element already under observation is a no-op, so a
    // blanket re-scan is cheaper than tracking which nodes are new.
    document.querySelectorAll(SELECTOR).forEach((el) => spotlight.observe(el));
  }

  function queueScan() {
    if (rescanQueued || !spotlight) return;
    rescanQueued = true;
    requestAnimationFrame(() => {
      rescanQueued = false;
      if (spotlight) scan();
    });
  }

  function start() {
    if (spotlight) return;

    spotlight = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          entry.target.classList.toggle(LIVE_CLASS, entry.isIntersecting);
        });
      },
      { rootMargin: BAND, threshold: 0 }
    );
    scan();

    // Route changes swap the whole page out; watch for the new cards rather
    // than asking every screen to announce itself.
    mounts = new MutationObserver(queueScan);
    mounts.observe(document.body, { childList: true, subtree: true });
  }

  function stop() {
    if (mounts) {
      mounts.disconnect();
      mounts = null;
    }
    if (spotlight) {
      spotlight.disconnect();
      spotlight = null;
    }
    document
      .querySelectorAll(`.${LIVE_CLASS}`)
      .forEach((el) => el.classList.remove(LIVE_CLASS));
  }

  const sync = () => (shouldRun() ? start() : stop());

  sync();

  // A mouse plugged into a tablet flips `hover`, and the OS motion setting can
  // change mid-session; follow both rather than deciding once at load.
  canHover.addEventListener("change", sync);
  reduced.addEventListener("change", sync);

  return () => {
    canHover.removeEventListener("change", sync);
    reduced.removeEventListener("change", sync);
    stop();
  };
}
